'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Pagination } from '@eliadmin/core/src/components/ui/pagination';
import { Skeleton } from '@eliadmin/core/src/components/ui/skeleton';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { normalizeRetiredJobportalSort } from '@eliadmin/core/src/utils/retired-list-sort';
import { useQueryClient, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { useSearchParams } from 'next/navigation';
import { useState, useEffect, useMemo } from 'react';
import { fetchCurriculums } from '@eliadmin/core/src/api/curriculum-vitaes/curriculums-api';
import {
  buildCurriculumsQuery,
  useJobportalCurriculumsStore,
} from '../../stores/jobportal-curriculums-store';
import { CurriculumVitaeCard } from './curriculum-vitae-card';

export type JobPortalCurriculumsContainerProps = {
  showPagination?: boolean;
  showLoadMore?: boolean;
  paginationKey?: string;
};

export function JobPortalCurriculumsContainer(props: JobPortalCurriculumsContainerProps) {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const authToken = useCustomerStore(s => s.me?.authToken);
  const customerId = useCustomerStore(s => s.me?.customer?.id);
  const searchParams = useSearchParams();
  const filter = normalizeRetiredJobportalSort(useJobportalCurriculumsStore(s => s.filter));
  const clearTrigger = useJobportalCurriculumsStore(s => s.clearTrigger);
  const applyFilter = useJobportalCurriculumsStore(s => s.applyFilter);

  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  const queryWithoutPage = useMemo(() => {
    if (!currentPage) return {};

    return { page: currentPage };
  }, [currentPage]);

  useEffect(() => {
    applyFilter({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearTrigger]);

  const queryClient = useQueryClient();
  const listingQueryKey = [
    CacheKey.JOBPPORTAL_OFFERS,
    'public-curriculums',
    'infinite',
    searchParams.toString(),
    currentPage,
    queryWithoutPage,
    clearTrigger,
    filter,
    locale,
    authToken ?? null,
    customerId ?? null,
  ];

  const {
    data: curriculums,
    fetchNextPage,
    isFetching,
    hasNextPage,
  } = useSuspenseInfiniteQuery({
    queryKey: listingQueryKey,
    queryFn: ({ pageParam }) =>
      fetchCurriculums({
        query: {
          ...queryWithoutPage,
          page: pageParam,
          ...buildCurriculumsQuery({
            ...filter,
            show_in_index: true,
          }),
          'order[]': ['desc', filter.order],
          'orderBy[]': ['topped_up_to', filter.orderBy],
          search: filter.search,
          results: 30,
        },
        locale,
        authToken,
      }),
    getNextPageParam: lastPage => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },

    initialPageParam: currentPage ? currentPage : 1,
  });

  const total = useMemo(
    () => curriculums?.pages[0].meta.total,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, queryWithoutPage, clearTrigger],
  );

  const showing = useMemo(
    () =>
      curriculums?.pages[curriculums?.pages.length - 1].meta.to -
      curriculums?.pages[0].meta.from +
      1,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentPage, queryWithoutPage, clearTrigger],
  );

  //   useEffect(() => {
  //     setSalaryRanges(jobCurriculumsQuery?.pages[0].salary_type);
  //     // eslint-disable-next-line react-hooks/exhaustive-deps
  //   }, [jobCurriculumsQuery]);

  const lastPage = curriculums?.pages[curriculums?.pages.length - 1];
  const flatList = curriculums?.pages.flatMap(page => page.data) ?? [];

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-6 @container',
        'ea-job-portal-curriculums-container',
      )}
    >
      {!flatList?.length && <p className="text-center text-muted-foreground">{t('text554')}</p>}

      <div
        className={cn(
          'grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3',
          'ea-job-portal-curriculums-container__grid',
        )}
      >
        {flatList?.map((curriculum, i) => (
          <CurriculumVitaeCard key={i} curriculum={curriculum} />
        ))}
      </div>

      {!!hasNextPage && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-curriculums-container__load-more',
          )}
        >
          <Button variant="secondary" onClick={() => fetchNextPage()}>
            {t('text338')}
          </Button>
        </div>
      )}

      {props.showPagination && (
        <div
          className={cn(
            'flex flex-row justify-center',
            'ea-job-portal-curriculums-container__pagination',
          )}
        >
          <Pagination
            links={lastPage?.meta.links}
            onPageClicked={page => {
              if (String(page) !== String(currentPage)) return;
              queryClient.resetQueries({
                queryKey: listingQueryKey,
                exact: true,
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

export function JobPortalCurriculumsContainerSkeleton() {
  const array = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div
      className={cn(
        'relative flex flex-col gap-6 @container',
        'ea-job-portal-curriculums-container',
      )}
    >
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5 sm:gap-y-8 lg:grid-cols-3',
          'ea-job-portal-curriculums-container__grid ea-job-portal-curriculums-container__grid--skeleton',
        )}
      >
        {array.map(i => (
          <Skeleton key={i} className="min-h-[400px] min-w-[250px]" />
        ))}
      </div>

      <Skeleton className="h-20 w-full" />
    </div>
  );
}
